import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Shield, ShieldAlert, ShieldCheck, UserPlus, LogIn, 
  MapPin, Globe, Monitor, Clock, AlertTriangle, RefreshCw,
  Eye, Filter, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Format date to IST display
const formatToIST = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

// Log entry card component
const LogEntry = ({ log, expanded, onToggle }) => {
  const isRegistration = log.activityType === 'registration';
  const isSuccess = log.status === 'success';
  const isSuspicious = log.isSuspicious;
  
  const getStatusColor = () => {
    if (isSuspicious) return 'bg-red-100 text-red-800 border-red-200';
    if (log.status === 'success') return 'bg-green-100 text-green-800 border-green-200';
    if (log.status === 'failed') return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };
  
  const getActivityIcon = () => {
    if (isSuspicious) return <ShieldAlert className="w-5 h-5 text-red-500" />;
    if (isRegistration) return <UserPlus className="w-5 h-5 text-blue-500" />;
    return <LogIn className="w-5 h-5 text-purple-500" />;
  };

  return (
    <Card className={`border ${isSuspicious ? 'border-red-300 bg-red-50/30' : 'border-gray-200'} transition-all duration-200`}>
      <CardContent className="p-3 sm:p-4">
        {/* Main row - always visible */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`p-2 rounded-lg ${isSuspicious ? 'bg-red-100' : isRegistration ? 'bg-blue-100' : 'bg-purple-100'}`}>
              {getActivityIcon()}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-semibold text-gray-900 text-sm sm:text-base">
                  {isRegistration ? 'New Registration' : 'Master Login'}
                </span>
                <Badge className={`${getStatusColor()} text-[10px] sm:text-xs`}>
                  {log.status.toUpperCase()}
                </Badge>
                {isSuspicious && (
                  <Badge className="bg-red-500 text-white text-[10px] sm:text-xs animate-pulse">
                    SUSPICIOUS
                  </Badge>
                )}
              </div>
              
              <div className="text-sm text-gray-600 truncate">
                {log.userEmail || 'Unknown'}
              </div>
              
              <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatToIST(log.createdAt)}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {log.location?.city || 'Unknown'}, {log.location?.country || ''}
                </span>
              </div>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="h-8 w-8 p-0 flex-shrink-0"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
        
        {/* Expanded details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            {/* User Details */}
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-700 flex items-center gap-1">
                <UserPlus className="w-4 h-4" /> User Details
              </h4>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p><span className="text-gray-500">Name:</span> {log.userName || 'N/A'}</p>
                <p><span className="text-gray-500">Email:</span> {log.userEmail || 'N/A'}</p>
                {log.userPhone && <p><span className="text-gray-500">Phone:</span> {log.userPhone}</p>}
                {log.venueName && <p><span className="text-gray-500">Venue:</span> {log.venueName}</p>}
                {log.venueLocation && <p><span className="text-gray-500">Location:</span> {log.venueLocation}</p>}
              </div>
            </div>
            
            {/* Location & Device */}
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-700 flex items-center gap-1">
                <Globe className="w-4 h-4" /> Location & Device
              </h4>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p><span className="text-gray-500">IP:</span> <code className="bg-gray-200 px-1 rounded">{log.ip}</code></p>
                <p><span className="text-gray-500">City:</span> {log.location?.city || 'Unknown'}</p>
                <p><span className="text-gray-500">Region:</span> {log.location?.region || 'Unknown'}</p>
                <p><span className="text-gray-500">Country:</span> {log.location?.country || 'Unknown'}</p>
                <p><span className="text-gray-500">ISP:</span> {log.location?.isp || 'Unknown'}</p>
                <p className="flex items-center gap-1">
                  <Monitor className="w-3 h-3" />
                  <span className="text-gray-500">Device:</span> {log.device?.device} - {log.device?.os} - {log.device?.browser}
                </p>
              </div>
            </div>
            
            {/* Suspicious Reason */}
            {log.isSuspicious && log.suspiciousReason && (
              <div className="col-span-full">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-red-700">Suspicious Activity Detected</h4>
                    <p className="text-red-600 text-sm">{log.suspiciousReason}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Failure Reason */}
            {log.failureReason && (
              <div className="col-span-full">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-700"><span className="font-semibold">Failure Reason:</span> {log.failureReason}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SecurityActivity = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [expandedLog, setExpandedLog] = useState(null);
  
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Fetch logs based on filter
      let logsUrl = `${API_URL}/api/security/logs?limit=50`;
      if (filter === 'registrations') {
        logsUrl = `${API_URL}/api/security/registrations?limit=50`;
      } else if (filter === 'master_logins') {
        logsUrl = `${API_URL}/api/security/master-logins?limit=50`;
      } else if (filter === 'suspicious') {
        logsUrl = `${API_URL}/api/security/suspicious?limit=50`;
      }
      
      const [logsRes, statsRes] = await Promise.all([
        fetch(logsUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/security/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (!logsRes.ok || !statsRes.ok) {
        throw new Error('Failed to fetch security data');
      }
      
      const logsData = await logsRes.json();
      const statsData = await statsRes.json();
      
      setLogs(logsData.logs || []);
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching security data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [filter]);
  
  const toggleExpand = (logId) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6 text-center">
          <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">{error}</p>
          <Button onClick={fetchData} variant="outline" className="mt-4">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-cyan-100">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600">Today&apos;s Registrations</p>
                <p className="text-2xl font-bold text-blue-700">{stats?.today?.registrations || 0}</p>
              </div>
              <UserPlus className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-indigo-100">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600">Master Logins Today</p>
                <p className="text-2xl font-bold text-purple-700">{stats?.today?.masterLogins || 0}</p>
              </div>
              <LogIn className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-rose-100">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600">Suspicious Today</p>
                <p className="text-2xl font-bold text-red-700">{stats?.today?.suspicious || 0}</p>
              </div>
              <ShieldAlert className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-yellow-100">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600">Flagged IPs</p>
                <p className="text-2xl font-bold text-amber-700">{stats?.total?.suspiciousIPs || 0}</p>
              </div>
              <Globe className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Weekly Summary */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-500" />
            Weekly Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-800">{stats?.week?.registrations || 0}</p>
            <p className="text-xs text-gray-500">New Registrations (7 days)</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">{stats?.week?.suspicious || 0}</p>
            <p className="text-xs text-gray-500">Suspicious Activities (7 days)</p>
          </div>
        </CardContent>
      </Card>
      
      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filter Activity:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Activity</SelectItem>
                  <SelectItem value="registrations">Registrations Only</SelectItem>
                  <SelectItem value="master_logins">Master Logins Only</SelectItem>
                  <SelectItem value="suspicious">Suspicious Only</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchData} className="h-9">
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Activity Logs */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Eye className="w-5 h-5" />
          Activity Log ({logs.length})
        </h3>
        
        {logs.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8 text-center">
              <ShieldCheck className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-gray-600">No activity logs found</p>
              <p className="text-gray-500 text-sm">Security logs will appear here as activities occur</p>
            </CardContent>
          </Card>
        ) : (
          logs.map((log) => (
            <LogEntry
              key={log.id}
              log={log}
              expanded={expandedLog === log.id}
              onToggle={() => toggleExpand(log.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default SecurityActivity;
